import { useState, useEffect, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical, Search, X } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  NONE,
  extractErr,
  RecordDrawer,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type Tag = {
  id: number; name: string; category: string; color: string
}

type PersonLite = {
  id: number
  full_name: string
  first_name?: string
  last_name?: string
  personal_email?: string
  current_title?: string
  current_company?: string
}

type CandidateProfile = {
  id: number
  person: number
  person_name: string
  person_email: string
  person_headline: string
  person_current_title: string
  person_current_company: string
  person_country_code: string
  recruitment_source: string
  recruitment_owner: number | null
  owner_name: string | null
  talent_pool_status: 'NONE' | 'SILVER' | 'GOLD'
  is_archived: boolean
  summary: string
  availability: string
  tags_detail: Tag[]
  open_applications_count: number
  total_applications_count: number
  last_application_at: string | null
  last_touch_at: string | null
  next_followup_at: string | null
  hired: boolean
  updated_at: string
}

type NewCandidateInput = {
  first_name: string
  last_name: string
  personal_email: string
  headline: string
  recruitment_source: string
  recruitment_owner: number | null
}

const TALENT_COLOR: Record<CandidateProfile['talent_pool_status'], string> = {
  NONE: 'bg-gray-100 text-gray-700',
  SILVER: 'bg-slate-200 text-slate-800',
  GOLD: 'bg-amber-100 text-amber-800',
}
const TALENT_LABEL: Record<CandidateProfile['talent_pool_status'], string> = {
  NONE: 'Standard', SILVER: 'Silver', GOLD: 'Gold',
}

const TAG_CATEGORY_COLOR: Record<string, string> = {
  SKILL: 'bg-violet-100 text-violet-700',
  SENIORITY: 'bg-purple-100 text-purple-700',
  LOCATION: 'bg-emerald-100 text-emerald-700',
  LANGUAGE: 'bg-cyan-100 text-cyan-700',
  DEPARTMENT: 'bg-orange-100 text-orange-700',
  OTHER: 'bg-gray-100 text-gray-700',
  '': 'bg-gray-100 text-gray-600',  // uncategorised
}

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'has_open', label: 'Has open application' },
  { value: 'hired', label: 'Hired' },
  { value: 'no_active', label: 'No active application' },
]

const POOL_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'Any tier' },
  { value: 'GOLD', label: 'Gold' },
  { value: 'SILVER', label: 'Silver' },
  { value: 'NONE', label: 'Standard' },
]

export default function ATSCandidates() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { confirm, dialog } = useDeleteConfirm()

  const [search, setSearch] = useState('')
  const [appStatus, setAppStatus] = useState('')
  const [pool, setPool] = useState('')
  const [owner, setOwner] = useState<number | null>(null)
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([])
  const [showArchived, setShowArchived] = useState(false)

  const query = (() => {
    const p = new URLSearchParams()
    if (search.trim()) p.append('search', search.trim())
    if (appStatus) p.append('app_status', appStatus)
    if (pool) p.append('talent_pool', pool)
    if (owner) p.append('owner', String(owner))
    selectedTagIds.forEach((id) => p.append('tag', String(id)))
    if (showArchived) p.append('archived', 'true')
    return p.toString()
  })()

  const candidatesQ = useQuery({
    queryKey: ['ats', 'candidates', query],
    queryFn: async () =>
      (await api.get<CandidateProfile[]>(`/ats/candidates/${query ? '?' + query : ''}`)).data,
  })
  const tagsQ = useQuery({
    queryKey: ['ats', 'tags'],
    queryFn: async () => (await api.get<Tag[]>('/ats/tags/')).data,
  })
  const personsQ = useQuery({
    queryKey: ['hr', 'persons'],
    queryFn: async () => (await api.get<PersonLite[]>('/hr/persons/')).data,
  })
  // Full unfiltered candidate set (incl. archived) — used by the duplicate
  // guard to know whether an existing Person already has a CandidateProfile.
  // The page's main `candidatesQ` is filtered by the user's facets and can't
  // be reused for this.
  const allCandidatesQ = useQuery({
    queryKey: ['ats', 'candidates', 'all-for-dedupe'],
    queryFn: async () =>
      (await api.get<{ id: number; person: number }[]>(
        '/ats/candidates/?archived=all',
      )).data,
  })

  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<NewCandidateInput>({
    first_name: '', last_name: '', personal_email: '',
    headline: '', recruitment_source: '', recruitment_owner: null,
  })

  const createMut = useMutation({
    mutationFn: async () => {
      const person = (await api.post('/hr/persons/', {
        first_name: draft.first_name,
        last_name: draft.last_name,
        personal_email: draft.personal_email,
        headline: draft.headline,
      })).data
      const profile = (await api.post('/ats/candidates/', {
        person: person.id,
        recruitment_source: draft.recruitment_source,
        recruitment_owner: draft.recruitment_owner,
      })).data
      return profile
    },
    onSuccess: (created: CandidateProfile) => {
      toast.success('Candidate added')
      qc.invalidateQueries({ queryKey: ['ats', 'candidates'] })
      qc.invalidateQueries({ queryKey: ['hr', 'persons'] })
      setCreating(false)
      navigate(`/admin/ats/candidates/${created.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  // Attach a CandidateProfile to an *existing* Person — used by the
  // duplicate-guard "Attach profile" button when the recruiter picks
  // a Person who isn't already in the talent pool.
  const attachMut = useMutation({
    mutationFn: async (personId: number) => (await api.post('/ats/candidates/', {
      person: personId,
      recruitment_source: draft.recruitment_source,
      recruitment_owner: draft.recruitment_owner,
    })).data,
    onSuccess: (created: CandidateProfile) => {
      toast.success('Candidate profile attached to existing person')
      qc.invalidateQueries({ queryKey: ['ats', 'candidates'] })
      setCreating(false)
      navigate(`/admin/ats/candidates/${created.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const archiveMut = useMutation({
    mutationFn: async ({ id, value }: { id: number; value: boolean }) =>
      (await api.patch(`/ats/candidates/${id}/`, { is_archived: value })).data,
    onSuccess: () => {
      toast.success('Updated')
      qc.invalidateQueries({ queryKey: ['ats', 'candidates'] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/ats/candidates/${id}/`),
    onSuccess: () => {
      toast.success('Candidate profile removed')
      qc.invalidateQueries({ queryKey: ['ats', 'candidates'] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const candidates = candidatesQ.data || []
  const tags = tagsQ.data || []

  // personId → existing CandidateProfile id (across active + archived).
  // Used so the duplicate guard knows whether to offer "open existing" vs
  // "attach a new profile".
  const profileByPersonId = new Map<number, number>()
  for (const c of (allCandidatesQ.data || [])) profileByPersonId.set(c.person, c.id)

  // Compute duplicate matches from the draft inputs. Match on:
  //   - exact email (case-insensitive)
  //   - substring on last_name OR first_name (>= 3 chars to avoid noise)
  const allPersons = personsQ.data || []
  const dupes: PersonLite[] = (() => {
    const out: PersonLite[] = []
    const email = (draft.personal_email || '').trim().toLowerCase()
    const last = (draft.last_name || '').trim().toLowerCase()
    const first = (draft.first_name || '').trim().toLowerCase()
    if (!email && last.length < 3 && first.length < 3) return out
    for (const p of allPersons) {
      const pe = (p.personal_email || '').toLowerCase()
      const pl = (p.last_name || '').toLowerCase()
      const pf = (p.first_name || '').toLowerCase()
      const emailHit = email && pe && pe === email
      const lastHit = last.length >= 3 && pl && pl.includes(last)
      const firstHit = first.length >= 3 && pf && pf.includes(first) && last.length >= 3 && pl === last
      if (emailHit || lastHit || firstHit) out.push(p)
      if (out.length >= 5) break
    }
    return out
  })()
  const persons = personsQ.data || []

  function toggleTag(id: number) {
    setSelectedTagIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id])
  }

  return (
    <AdminLayout>
      {dialog}

      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Candidates</h2>
          <p className="text-sm text-muted-foreground">
            {candidates.length} {showArchived ? 'archived' : 'active'} · search the talent pool
          </p>
        </div>
        <Button onClick={() => setCreating(true)}><Plus className="h-3 w-3 mr-1" /> New candidate</Button>
      </div>

      {/* Search + facets — two-tier layout. Search is the headline action,
          everything else is secondary refinement. */}
      <div className="space-y-2 mb-4">
        {/* Primary row: the search bar, dominant. */}
        <CandidateSearchBar
          value={search}
          onChange={setSearch}
          resultCount={candidates.length}
        />

        {/* Secondary row: refinement facets in small controls. */}
        <div className="flex flex-wrap gap-2 items-center text-xs text-muted-foreground">
          <span className="mr-1">Refine:</span>
          <Select value={appStatus || NONE} onValueChange={(v) => setAppStatus(v === NONE ? '' : v)}>
            <SelectTrigger className="w-44 h-8 text-xs"><SelectValue placeholder="Application status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>All applications</SelectItem>
              {STATUS_FILTERS.filter((s) => s.value).map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={pool || NONE} onValueChange={(v) => setPool(v === NONE ? '' : v)}>
            <SelectTrigger className="w-32 h-8 text-xs"><SelectValue placeholder="Talent pool" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Any tier</SelectItem>
              {POOL_FILTERS.filter((s) => s.value).map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <SearchableSelect
            className="w-36 h-8 text-xs"
            value={owner ? String(owner) : NONE}
            onChange={(v) => setOwner(v === NONE ? null : Number(v))}
            placeholder="Owner"
            options={[
              { value: NONE, label: 'Any owner' },
              ...persons.map((p) => ({ value: String(p.id), label: p.full_name })),
            ]}
          />
          <label className="flex items-center gap-1 cursor-pointer ml-1">
            <input
              type="checkbox" checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            Show archived
          </label>
          {(search || appStatus || pool || owner || selectedTagIds.length > 0 || showArchived) && (
            <button
              type="button"
              onClick={() => {
                setSearch(''); setAppStatus(''); setPool('');
                setOwner(null); setSelectedTagIds([]); setShowArchived(false)
              }}
              className="ml-auto text-violet-600 hover:underline"
            >
              Clear all filters
            </button>
          )}
        </div>

        {tags.length > 0 && (
          <div className="flex flex-wrap items-center gap-1 pt-1">
            <span className="text-xs text-muted-foreground mr-2">Tags:</span>
            {tags.map((t) => {
              const active = selectedTagIds.includes(t.id)
              const color = TAG_CATEGORY_COLOR[t.category] || TAG_CATEGORY_COLOR['']
              return (
                <button
                  key={t.id} onClick={() => toggleTag(t.id)}
                  className={`text-xs px-2 py-0.5 rounded font-semibold border ${active ? 'border-violet-500 ring-1 ring-violet-200 ' + color : 'border-transparent ' + color}`}
                  title={t.category || 'Uncategorised — admin can curate'}
                >
                  {t.name}
                </button>
              )
            })}
            {selectedTagIds.length > 0 && (
              <button onClick={() => setSelectedTagIds([])}
                className="text-xs text-violet-600 hover:underline ml-2">
                Clear tags
              </button>
            )}
          </div>
        )}
      </div>

      {candidatesQ.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : candidates.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">
          No candidates match this filter. Try clearing search / tags or {' '}
          <button className="text-violet-600 hover:underline" onClick={() => setCreating(true)}>
            adding a candidate
          </button>.
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>Name</Th>
                <Th>Headline / role</Th>
                <Th>Tags</Th>
                <Th>Pool</Th>
                <Th>Owner</Th>
                <Th className="text-right">Open</Th>
                <Th className="text-right">Total</Th>
                <Th>Last touch / follow-up</Th>
                <Th>Last activity</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {candidates.map((c) => (
                <tr
                  key={c.id}
                  className="hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/admin/ats/candidates/${c.id}`)}
                >
                  <Td>
                    <div className="font-medium text-foreground">{c.person_name}</div>
                    {c.person_email && <div className="text-xs text-muted-foreground">{c.person_email}</div>}
                  </Td>
                  <Td className="text-sm">
                    {c.person_headline || (c.person_current_title || c.person_current_company)
                      ? <>
                          <div>{c.person_headline || c.person_current_title}</div>
                          {c.person_current_company && (
                            <div className="text-xs text-muted-foreground">{c.person_current_company}</div>
                          )}
                        </>
                      : <span className="text-muted-foreground">—</span>}
                  </Td>
                  <Td>
                    <div className="flex flex-wrap gap-1">
                      {c.tags_detail.slice(0, 4).map((t) => (
                        <span key={t.id}
                          className={`text-xs px-1.5 py-0.5 rounded font-semibold ${TAG_CATEGORY_COLOR[t.category] || TAG_CATEGORY_COLOR['']}`}>
                          {t.name}
                        </span>
                      ))}
                      {c.tags_detail.length > 4 && (
                        <span className="text-xs text-muted-foreground">+{c.tags_detail.length - 4}</span>
                      )}
                    </div>
                  </Td>
                  <Td>
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${TALENT_COLOR[c.talent_pool_status]}`}>
                      {TALENT_LABEL[c.talent_pool_status]}
                    </span>
                  </Td>
                  <Td className="text-sm">{c.owner_name || <span className="text-muted-foreground">—</span>}</Td>
                  <Td className="text-right">
                    {c.open_applications_count > 0
                      ? <span className="text-violet-700 font-semibold">{c.open_applications_count}</span>
                      : <span className="text-muted-foreground">0</span>}
                  </Td>
                  <Td className="text-right text-sm">{c.total_applications_count}</Td>
                  <Td className="text-xs">
                    <TouchCell lastTouchAt={c.last_touch_at} nextFollowupAt={c.next_followup_at} />
                  </Td>
                  <Td className="text-xs">
                    {c.last_application_at
                      ? new Date(c.last_application_at).toLocaleDateString()
                      : <span className="text-muted-foreground">—</span>}
                    {c.hired && <span className="ml-2 text-xs px-1 py-0.5 rounded font-semibold bg-green-100 text-green-800">hired</span>}
                  </Td>
                  <Td className="text-right" onClick={(ev) => ev.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/admin/ats/candidates/${c.id}`)}>
                          Open
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => archiveMut.mutate({ id: c.id, value: !c.is_archived })}
                        >
                          {c.is_archived ? 'Unarchive' : 'Archive'}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => confirm(
                            `candidate profile for ${c.person_name}`,
                            () => deleteMut.mutate(c.id),
                          )}
                          className="text-destructive"
                        >
                          Delete profile
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title="New candidate"
        description="Add a sourced candidate to the talent pool. Their Person record is created in HR; the recruitment profile is created on top. You can attach tags + applications on the record page."
        onSubmit={() => createMut.mutate()}
        isPending={createMut.isPending}
        submitLabel="Add"
      >
        <FieldGroup>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="c-first">First name *</FieldLabel>
              <Input id="c-first" required value={draft.first_name}
                onChange={(e) => setDraft({ ...draft, first_name: e.target.value })} />
            </Field>
            <Field>
              <FieldLabel htmlFor="c-last">Last name *</FieldLabel>
              <Input id="c-last" required value={draft.last_name}
                onChange={(e) => setDraft({ ...draft, last_name: e.target.value })} />
            </Field>
          </div>
          <Field>
            <FieldLabel htmlFor="c-email">Personal email</FieldLabel>
            <Input id="c-email" type="email" value={draft.personal_email}
              onChange={(e) => setDraft({ ...draft, personal_email: e.target.value })} />
          </Field>

          {dupes.length > 0 && (
            <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm">
              <div className="font-semibold text-amber-900 mb-1">
                Possible duplicate{dupes.length === 1 ? '' : 's'} in HR
              </div>
              <p className="text-xs text-amber-800 mb-2">
                We already have {dupes.length === 1 ? 'a Person' : `${dupes.length} people`} with matching name or email.
                Use the existing record instead of creating a duplicate.
              </p>
              <ul className="space-y-1">
                {dupes.map((p) => {
                  const existingProfileId = profileByPersonId.get(p.id)
                  return (
                    <li key={p.id} className="flex items-center justify-between gap-2 bg-card rounded px-2 py-1.5 border border-amber-200">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{p.full_name}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {p.personal_email || '—'}
                          {p.current_title && <span> · {p.current_title}</span>}
                          {p.current_company && <span> @ {p.current_company}</span>}
                        </div>
                      </div>
                      {existingProfileId ? (
                        <Button size="sm" variant="outline"
                          onClick={() => {
                            setCreating(false)
                            navigate(`/admin/ats/candidates/${existingProfileId}`)
                          }}>
                          Open profile
                        </Button>
                      ) : (
                        <Button size="sm"
                          onClick={() => attachMut.mutate(p.id)}
                          disabled={attachMut.isPending}
                          title="Create a CandidateProfile on this existing Person">
                          Attach profile
                        </Button>
                      )}
                    </li>
                  )
                })}
              </ul>
              <p className="text-xs text-amber-700 mt-2">
                Or fill the form below to create a new Person anyway.
              </p>
            </div>
          )}

          <Field>
            <FieldLabel htmlFor="c-head">Headline</FieldLabel>
            <Input id="c-head" value={draft.headline}
              onChange={(e) => setDraft({ ...draft, headline: e.target.value })}
              placeholder="Senior React engineer, Bucharest" />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="c-src">Source</FieldLabel>
              <Input id="c-src" value={draft.recruitment_source}
                onChange={(e) => setDraft({ ...draft, recruitment_source: e.target.value })}
                placeholder="LinkedIn / Referral / Direct" />
            </Field>
            <Field>
              <FieldLabel htmlFor="c-own">Owner</FieldLabel>
              <SearchableSelect
                id="c-own"
                value={draft.recruitment_owner ? String(draft.recruitment_owner) : NONE}
                onChange={(v) => setDraft({ ...draft, recruitment_owner: v === NONE ? null : Number(v) })}
                placeholder="— none —"
                options={[
                  { value: NONE, label: '— none —' },
                  ...persons.map((p) => ({ value: String(p.id), label: p.full_name })),
                ]}
              />
              <FieldDescription>The recruiter responsible for nurturing this candidate.</FieldDescription>
            </Field>
          </div>
        </FieldGroup>
      </RecordDrawer>
    </AdminLayout>
  )
}

const SEARCH_INPUT_ID = 'ats-candidates-search'

function CandidateSearchBar({
  value, onChange, resultCount,
}: {
  value: string
  onChange: (v: string) => void
  resultCount: number
}) {
  // ⌘K / Ctrl+K focuses the search box from anywhere on the page.
  // We query the input by id rather than using a ref because shadcn's
  // <Input> here isn't a forwardRef component.
  useEffect(() => {
    function focusBox() {
      const el = document.getElementById(SEARCH_INPUT_ID) as HTMLInputElement | null
      el?.focus(); el?.select()
    }
    function onKey(e: KeyboardEvent) {
      const isMac = navigator.platform.toLowerCase().includes('mac')
      const cmdOrCtrl = isMac ? e.metaKey : e.ctrlKey
      if (cmdOrCtrl && e.key.toLowerCase() === 'k') {
        e.preventDefault(); focusBox()
      }
      if (e.key === 'Escape' && document.activeElement?.id === SEARCH_INPUT_ID && value) {
        e.preventDefault()
        onChange('')
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [value, onChange])

  const isMac = typeof navigator !== 'undefined' && navigator.platform.toLowerCase().includes('mac')

  return (
    <div className="flex items-center gap-3">
      <div className="relative flex-1 min-w-[420px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          id={SEARCH_INPUT_ID}
          placeholder="Search candidates by name, email, current title or company, headline, tags, summary, notes…"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="pl-10 pr-24 h-10 text-sm focus-visible:ring-2 focus-visible:ring-violet-400"
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {value ? (
            <button
              type="button"
              onClick={() => onChange('')}
              className="p-1 rounded hover:bg-muted text-muted-foreground"
              title="Clear search (Esc)"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          ) : (
            <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-border bg-muted text-[10px] font-mono text-muted-foreground">
              {isMac ? '⌘' : 'Ctrl'} K
            </kbd>
          )}
        </div>
      </div>
      <div className="text-xs text-muted-foreground whitespace-nowrap">
        <span className="font-semibold text-foreground">{resultCount}</span>
        {' '}
        {resultCount === 1 ? 'candidate' : 'candidates'}
        {value && <span> · matching “{value}”</span>}
      </div>
    </div>
  )
}

function TouchCell({
  lastTouchAt, nextFollowupAt,
}: { lastTouchAt: string | null; nextFollowupAt: string | null }) {
  // Top line: follow-up state (overdue / due today / future) — drives the row's urgency.
  // Bottom line: last touch age, stale if > 30 days or never.
  const now = new Date(); now.setHours(0, 0, 0, 0)
  let followup: ReactNode = null
  if (nextFollowupAt) {
    const d = new Date(nextFollowupAt); d.setHours(0, 0, 0, 0)
    const days = Math.round((d.getTime() - now.getTime()) / 86_400_000)
    if (days < 0) {
      followup = (
        <span className="inline-block px-1 py-0.5 rounded font-semibold bg-red-100 text-red-700">
          Overdue {Math.abs(days)}d
        </span>
      )
    } else if (days === 0) {
      followup = (
        <span className="inline-block px-1 py-0.5 rounded font-semibold bg-amber-100 text-amber-800">
          Due today
        </span>
      )
    } else {
      followup = (
        <span className="inline-block px-1 py-0.5 rounded font-medium bg-violet-100 text-violet-700">
          In {days}d
        </span>
      )
    }
  }

  let touch: ReactNode
  if (!lastTouchAt) {
    touch = <span className="text-amber-700">No touches yet</span>
  } else {
    const days = Math.floor((Date.now() - new Date(lastTouchAt).getTime()) / 86_400_000)
    const stale = days > 30
    touch = (
      <span className={stale ? 'text-amber-700' : 'text-muted-foreground'}>
        {days === 0 ? 'today' : days === 1 ? 'yesterday' : `${days}d ago`}
      </span>
    )
  }

  return (
    <div className="space-y-0.5">
      {followup && <div>{followup}</div>}
      <div>{touch}</div>
    </div>
  )
}

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-sm text-foreground ${className}`} onClick={onClick}>{children}</td>
}
